import { useContext, useEffect, useState } from 'react';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import Calendar from '@acko-tech/building-blocks.ui.calendar';
import PlansHeader from './header';
import DocumentUploadWidget from 'widgets/documentUpload';
import SnackBar from 'library/snackbar';
import {
    SegmentPages,
    SegmentTrackEvents,
    SegmentViewEvents,
} from 'utils/segment/constants';
import { MPosContext } from 'utils/context';
import { useSegmentViewEvents } from 'hooks/useSegmentView';
import { DeviceCategory, NewPlanStages } from '../constants';
import { getYYYY_MM_DD } from 'utils/date';
import {
    fetchDocumentList,
    findDocumentById,
    getLabels,
    IDocumentList,
    isEWPlan,
} from './utils';
import { getImageBasePath, uuid } from 'utils/generics';
import {
    Dropdown,
    IDropdownListItem,
} from '@acko-tech/building-blocks.ui.dropdown';
import { useRouter } from 'next/router';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import { newPlanUrls } from 'utils/api/urls';
import * as Styles from './styles';
import { getDomainOrigin } from 'utils/serverConfig';
import { useViewport } from 'utils/responsive';
import { ValidateGSTDetailsResponse } from 'utils/api/types';
import { callValidateGSTDetails } from 'utils/api/services';
import usePermissionHook from 'hooks/usePermissionHook';
import { PageLoader } from 'utils/styles';

const WarrantyOptions = [
    {
        value: '1',
        displayValue: '1 year',
    },
];

function DocumentUpload() {
    const router = useRouter();
    const {
        dealerType,
        partnerId,
        planDetails,
        setPlanDetails,
        setActiveStage,
        permissions,
        setGstDetailsValid,
        userDetails,
    } = useContext(MPosContext);

    const ewPlan = isEWPlan(planDetails.plan.plan_id?.toLowerCase());

    const [isIssuePlanButtonDisabled, setIsIssuePlanButtonDisabled] =
        useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [selectedWarranty, setSelectedWarranty] = useState({
        value: '',
        displayValue: '',
    });
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });

    const [documentUploadStatus, setDocumentUploadStatus] = useState({});

    const { isMobile } = useViewport();
    const { getPartnerIdFromPermission } = usePermissionHook();

    const { headerText } = getLabels(partnerId);

    const [documentList, setDocumentList] = useState<IDocumentList[]>([]);

    /**
     * view segment hook
     */
    useSegmentViewEvents(SegmentViewEvents.VIEW_UPLOAD_DOCUMENTS, {
        page: SegmentPages.NEW_PLAN_DOCUMENT_UPLOAD,
        product: 'pos',
        journey: 'buy',
        platform: 'mweb',
        partner: partnerId,
        type: dealerType,
        plan_id: planDetails.plan.plan_id,
        category: planDetails.device.type,
        device_model: planDetails.device.name,
        serial_numbers: [planDetails.device.serial_number],
    });

    const fetchDocuments = async () => {
        setPageLoading(true);
        const documentList = await fetchDocumentList(
            partnerId,
            planDetails.plan.plan_id,
            planDetails.plan.program_id
        );
        console.log('Client ', documentList);
        setDocumentList(documentList);
        let uploadStatus = {};
        documentList.forEach((document) => {
            uploadStatus = {
                ...uploadStatus,
                [document.id]: '',
            };
        });
        setDocumentUploadStatus({ ...uploadStatus });
        setPageLoading(false);
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    useEffect(() => {
        if (pageLoading) {
            return;
        }
        if (planDetails?.device?.manufacturer_warranty) {
            let value = '';
            let displayValue = '';
            const { years } = planDetails?.device?.manufacturer_warranty;
            if (years > 0) {
                value = years?.toString();
                displayValue = `${years?.toString()} ${
                    years > 1 ? 'years' : 'year'
                }`;
            }
            setSelectedWarranty({
                value,
                displayValue,
            });
        }
        if (planDetails?.document_urls) {
            const filledDocumentList = documentList.map((doc) => ({
                ...doc,
                url: planDetails.document_urls[doc.id] || '',
                name: planDetails.document_names[doc.id] || '',
            }));
            setDocumentList([...filledDocumentList]);
        }
    }, [planDetails, pageLoading]);

    useEffect(() => {
        if (pageLoading) {
            return;
        }
        const requiredDocumentList = documentList.map((doc) => doc.id);
        const areAllDocumentsUploaded = requiredDocumentList.every(
            (documentId) => !!planDetails.document_urls[documentId]
        );

        if (!areAllDocumentsUploaded) {
            setIsIssuePlanButtonDisabled(true);
            return;
        }
        if (ewPlan) {
            if (
                planDetails.device.manufacturer_warranty?.years &&
                planDetails.device.purchase_date?.length > 0
            ) {
                setIsIssuePlanButtonDisabled(false);
            } else {
                setIsIssuePlanButtonDisabled(true);
            }
        } else {
            setIsIssuePlanButtonDisabled(false);
        }
    }, [JSON.stringify(planDetails), pageLoading]);

    console.log('Upload status', documentUploadStatus);

    useEffect(() => {
        if (pageLoading) {
            return;
        }
        if (planDetails.document_urls) {
            const requiredDocumentList = documentList.map((doc) => doc.id);
            console.log(requiredDocumentList);
            setDocumentUploadStatus((prevStatus) => {
                const updatedStatus = requiredDocumentList.reduce(
                    (acc, documentId) => {
                        const isUploaded =
                            !!planDetails.document_urls[documentId];
                        acc[documentId] = isUploaded ? 'uploaded' : '';
                        return acc;
                    },
                    { ...prevStatus }
                );

                return updatedStatus;
            });
        }
    }, [JSON.stringify(planDetails.document_urls), pageLoading]);

    const onSelectFile = (id: string, file: any) => {
        /**
         * Segment event
         */
        const document = findDocumentById(documentList, id);
        if (document) {
            const eventName = document.segmentEvent;

            trackEvent(eventName, {
                page: SegmentPages.NEW_PLAN_DOCUMENT_UPLOAD,
                partner: partnerId,
                product: 'pos',
                journey: 'buy',
                platform: 'mweb',
                type: dealerType,
                plan_id: planDetails.plan.plan_id,
                device_model: planDetails.device.name,
                category: planDetails.device.type,
                serial_numbers: [planDetails.device.serial_number],
            });

            setDocumentList((documentList) =>
                documentList.map((document) => {
                    if (document.id === id) {
                        return {
                            ...document,
                            name: file.name,
                        };
                    }
                    return {
                        ...document,
                    };
                })
            );
            import('utils/api/services').then(async (req) => {
                const documentType = document?.type;

                setDocumentUploadStatus((documentUploadStatus) => ({
                    ...documentUploadStatus,
                    [id]: 'uploading',
                }));

                const { status, data, statusCode } = await req.uploadDocument(
                    `${getDomainOrigin()}/${newPlanUrls.documentUpload}`,
                    file,
                    documentType,
                    userDetails.user_id,
                    () => {}
                );
                if (statusCode === 401) {
                    router.push('/login');
                    setSnackbar({
                        type: 'failure',
                        message: 'Token expired. Redirecting to login page.',
                    });
                    return;
                }

                if (status && data?.url) {
                    const { url } = data;

                    setDocumentList((documentList) =>
                        documentList.map((document) => {
                            if (document.id === id) {
                                return {
                                    ...document,
                                    url,
                                };
                            }
                            return {
                                ...document,
                            };
                        })
                    );

                    setDocumentUploadStatus((documentUploadStatus) => ({
                        ...documentUploadStatus,
                        [id]: 'uploaded',
                    }));
                    setPlanDetails({
                        ...planDetails,
                        document_urls: {
                            ...planDetails.document_urls,
                            [id]: url,
                        },
                        document_names: {
                            ...planDetails.document_names,
                            [id]: file.name,
                        },
                    });
                    return;
                }

                setDocumentUploadStatus((documentUploadStatus) => ({
                    ...documentUploadStatus,
                    [id]: 'failed',
                }));
            });
        }
    };

    const onDelete = (e: any, id: string) => {
        e?.stopPropagation();
        setPlanDetails({
            ...planDetails,
            document_names: {
                ...planDetails.document_names,
                [id]: '',
            },
        });

        setDocumentUploadStatus((documentUploadStatus) => ({
            ...documentUploadStatus,
            [id]: '',
        }));
        setDocumentList((documentList) =>
            documentList.map((document) => {
                if (document.id === id) {
                    return {
                        ...document,
                        name: '',
                        url: '',
                    };
                }
                return {
                    ...document,
                };
            })
        );
        setPlanDetails({
            ...planDetails,
            document_urls: {
                ...planDetails.document_urls,
                [id]: '',
            },
            document_names: {
                ...planDetails.document_names,
                [id]: '',
            },
        });
    };

    const onDateSelect = (date: Date) => {
        setPlanDetails({
            ...planDetails,
            device: {
                ...planDetails.device,
                purchase_date: date.toISOString(),
            },
        });
    };

    const onChangeWarranty = ({ value, displayValue }: IDropdownListItem) => {
        setSelectedWarranty({
            value,
            displayValue,
        });
        setPlanDetails({
            ...planDetails,
            device: {
                ...planDetails.device,
                manufacturer_warranty: {
                    years: Number(value),
                    months: 0,
                    days: 0,
                },
            },
        });
    };

    const handleSnakbarComplete = () => {
        setSnackbar({
            type: '',
            message: '',
        });
    };

    const onSubmit = async () => {
        /**
         * Segment event
         */
        trackEvent(SegmentTrackEvents.TAP_CONTINUE_UPLOAD_DOCUMENTS_PAGE, {
            page: SegmentPages.NEW_PLAN_SERIAL_NUMBER,
            partner: partnerId,
            product: 'pos',
            journey: 'buy',
            platform: 'mweb',
            type: dealerType,
            plan_id: planDetails.plan.plan_id,
            device_model: planDetails.device.name,
            category: planDetails.device.type,
            serial_numbers: [planDetails.device.serial_number],
        });
        setIsLoading(true);
        setIsIssuePlanButtonDisabled(true);

        const gstValidationResponse: ValidateGSTDetailsResponse =
            await callValidateGSTDetails(
                getPartnerIdFromPermission(),
                permissions.group,
                false
            );

        if (!gstValidationResponse.is_error) {
            if (gstValidationResponse.data) {
                const { required, valid } = gstValidationResponse.data;
                if (required && !valid) {
                    setGstDetailsValid(false);
                    return;
                }
                setGstDetailsValid(true);
            }
        }

        import('./apis').then(async (req) => {
            const orderDetails = {
                customer: {
                    phone: planDetails.customer.phone,
                    email: planDetails.customer.email,
                    name: planDetails.customer.name,
                },
                device: {
                    model_id: planDetails.device.id,
                    name: planDetails.device.name,
                    category: planDetails.device.type,
                    serial_number: planDetails.device.serial_number,
                    purchase_date:
                        getYYYY_MM_DD(
                            new Date(
                                planDetails.device.purchase_date || new Date()
                            )
                        ) || getYYYY_MM_DD(new Date()),
                    delivery_date: getYYYY_MM_DD(new Date()),
                    price: planDetails.device.price,
                    brand: planDetails.device.brand,
                    manufacturer_warranty:
                        planDetails.device.manufacturer_warranty,
                    promoter_id: planDetails.device.promoter_id,
                },
                document_urls: planDetails.document_urls,
                plan: {
                    partner: partnerId,
                    plan_id: planDetails.plan.plan_id,
                    plan_name: planDetails.plan.plan_name,
                    premium: planDetails.plan.premium,
                    promo_name: planDetails.plan.promo_name,
                    insurance_program_id: planDetails.plan.program_id,
                    insurance_order_id: uuid(10),
                    insurance_program_name: 'ACKO INSURANCE',
                    plan_purchase_date: getYYYY_MM_DD(new Date()),
                    plan_category: planDetails.plan.plan_category,
                    ...(planDetails.plan.product_code && {
                        product_code: planDetails.plan.product_code,
                    }),
                    emi_enabled: planDetails.plan.emi_enabled,
                    payment_mode: planDetails.plan.payment_mode,
                    emi_selected: planDetails.plan.emi_selected,
                },
                order_communication: false,
                policy_communication: false,
            };
            const { status, data, statusCode, errorMessage } =
                await req.postOrderDetails(
                    orderDetails,
                    partnerId,
                    permissions.group
                );
            if (statusCode === 401) {
                router.push('/login');
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            if (statusCode === 409) {
                setSnackbar({
                    type: 'failure',
                    message:
                        errorMessage ||
                        `Policy is already exist for this ${
                            planDetails?.device?.type ===
                                DeviceCategory.MOBILE ||
                            planDetails?.device?.type === DeviceCategory.TABLET
                                ? 'IMEI'
                                : 'Serial'
                        }  number`,
                });
                setIsLoading(false);
                return;
            }
            if (status && data) {
                const { order_ekey } = data;
                setPlanDetails({
                    ...planDetails,
                    order_ekey,
                });
                if (permissions.purchase_journey === 'v0') {
                    setActiveStage(NewPlanStages.CUSTOMER_LINK);
                } else {
                    setActiveStage(NewPlanStages.SUMMARY);
                }
                setIsLoading(false);
                return;
            }
            setSnackbar({
                type: 'failure',
                message: errorMessage || 'Oops. Something went wrong.',
            });
        });
        setIsIssuePlanButtonDisabled(false);
        setIsLoading(false);
    };

    if (pageLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    return (
        <Styles.Wrapper>
            <PlansHeader />
            <Styles.Title>{headerText}</Styles.Title>

            {documentList.map((document: IDocumentList) => {
                console.log(document);
                console.log(documentUploadStatus[document.id]);
                return (
                    <Styles.DocumentUploadWrapper key={document.id}>
                        <DocumentUploadWidget
                            id={document.id}
                            title={document.documentText}
                            label={document.documentHelperText}
                            documentName={
                                planDetails?.document_names[document.id] || ''
                            }
                            uploadStatus={documentUploadStatus[document.id]}
                            isMandatory={document.mandatory}
                            onSelect={onSelectFile}
                            onDelete={onDelete}
                        />
                        {!ewPlan && document.warningNote && (
                            <Styles.WarningNote>
                                Date of invoice must be today
                            </Styles.WarningNote>
                        )}
                    </Styles.DocumentUploadWrapper>
                );
            })}

            {ewPlan && (
                <>
                    <Styles.DocumentUploadWrapper>
                        <Calendar
                            title={'Purchase date of the device'}
                            defaultDate={
                                planDetails?.device?.purchase_date
                                    ? new Date(planDetails.device.purchase_date)
                                    : undefined
                            }
                            onSelect={(date) => {
                                onDateSelect(date);
                            }}
                            enableWindow={{
                                start: new Date(
                                    new Date().setFullYear(
                                        new Date().getFullYear() - 100
                                    )
                                ),
                                current: new Date(),
                                end: new Date(),
                            }}
                            isDesktop={!isMobile}
                        />
                    </Styles.DocumentUploadWrapper>
                    <Styles.WarningNote>
                        Please ensure the date of purchase matches with date of
                        purchase mentioned in the invoice, discrepancy may lead
                        issue in Claims process.
                    </Styles.WarningNote>
                    <Styles.DocumentUploadWrapper>
                        <Dropdown
                            value={selectedWarranty}
                            heading={'Manufacturer’s warranty'}
                            label={`Manufacturer’s warranty`}
                            options={WarrantyOptions}
                            onChange={onChangeWarranty}
                        />
                    </Styles.DocumentUploadWrapper>
                </>
            )}
            <Styles.SubmitButtonWrapper>
                <PrimaryButton
                    disabled={isIssuePlanButtonDisabled}
                    loading={isLoading}
                    onClick={onSubmit}
                >
                    Continue
                </PrimaryButton>
            </Styles.SubmitButtonWrapper>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={handleSnakbarComplete}
            />
        </Styles.Wrapper>
    );
}

export default DocumentUpload;
